import { useEffect, useMemo, useState } from "react";
import styles from "./HomepageAssessmentViewer.module.css";

const EMPTY = "—";

const isMissingValue = (value) =>
  value == null || (typeof value === "string" && value.trim() === "");

const toNumberOrNull = (value) => {
  if (isMissingValue(value)) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatInteger = (value) => {
  if (isMissingValue(value)) return EMPTY;
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY;
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num);
};

const formatPercent = (value, digits = 1) => {
  if (isMissingValue(value)) return EMPTY;
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY;
  return `${(num * 100).toFixed(digits)}%`;
};

const formatSeconds = (value) => {
  if (isMissingValue(value)) return EMPTY;
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY;
  if (num >= 60) {
    const minutes = Math.floor(num / 60);
    const seconds = Math.round(num % 60);
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }
  return `${Math.round(num)}s`;
};

const formatMilliseconds = (value) => {
  if (isMissingValue(value)) return EMPTY;
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}s`;
  return `${Math.round(num)}ms`;
};

const formatDecimal = (value, digits = 1) => {
  if (isMissingValue(value)) return EMPTY;
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY;
  return num.toFixed(digits);
};

const formatTitle = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const toneClass = (tone = "neutral") => {
  switch (tone) {
    case "positive":
      return styles.statusPositive;
    case "warning":
      return styles.statusWarning;
    case "negative":
      return styles.statusNegative;
    default:
      return styles.statusNeutral;
  }
};

const scoreClass = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  if (num >= 85) return styles.scorePositive;
  if (num >= 60) return styles.scoreWarning;
  return styles.scoreNegative;
};

const summaryToneClass = (tone = "neutral") => {
  switch (tone) {
    case "warning":
      return styles.summaryCardWarning;
    case "negative":
      return styles.summaryCardNegative;
    default:
      return "";
  }
};

const buildHeroStatus = (hero = {}) => {
  const alt = String(hero?.alt_status || "").toLowerCase();
  const quality = String(hero?.quality || "").toLowerCase();
  const alignment = String(hero?.product_alignment || "").toLowerCase();

  if (alt === "missing" || alt === "poor") return { label: "Needs attention", tone: "warning" };
  if (alignment === "not_aligned" || quality === "poor") return { label: "At risk", tone: "negative" };
  return { label: "Healthy", tone: "positive" };
};

const renderFieldPills = (fields = []) => {
  if (!Array.isArray(fields) || !fields.length) return null;
  return (
    <div className={styles.fieldPills}>
      {fields.map((field, index) => (
        <span key={`${field}-${index}`} className={styles.fieldPill}>
          + {field}
        </span>
      ))}
    </div>
  );
};

const getHeroImageUrl = (hero = {}) => {
  const value = hero?.url || hero?.image_url || hero?.src || "";
  return typeof value === "string" && value.trim() ? value.trim() : "";
};

const getHeroCardTitle = (hero = {}, index = 0) => {
  const explicitLabel = String(hero?.device_label || "").trim();
  if (explicitLabel) return explicitLabel;

  const variant = String(hero?.device_variant || "").trim().toLowerCase();
  if (variant === "desktop") return "Desktop hero";
  if (variant === "mobile") return "Mobile hero";

  return `Image ${hero.id || index + 1}`;
};

const getInventoryTypeLabel = (image = {}) => {
  const linkedType = String(image?.linked_page_type || "").trim().toLowerCase();
  if (linkedType === "collection") return "Collection";
  if (linkedType === "product") return "Product";
  return formatTitle(image?.type) || "Other";
};

const getInventoryLinkLabel = (image = {}) => {
  const linkedType = String(image?.linked_page_type || "").trim().toLowerCase();
  const handle = String(image?.linked_page_handle || "").trim();
  if (linkedType === "collection") {
    return handle ? `Collection: ${formatTitle(handle)}` : "Collection page";
  }
  if (linkedType === "product") {
    return handle ? `Product: ${formatTitle(handle)}` : "Product page";
  }
  return image?.linked_page_url ? "Linked page" : "";
};

const formatRangeLabel = (start, end) => {
  if (!start || !end) return "";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return "";
  return `${startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} - ${endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

const resolveTimeframe = (timeframe = {}) => {
  const primaryLabel =
    timeframe?.label ||
    timeframe?.window?.label ||
    formatRangeLabel(timeframe?.window?.start, timeframe?.window?.end) ||
    "Last 28 days";
  const comparisonLabel =
    timeframe?.comparisonLabel ||
    timeframe?.comparison_window?.label ||
    formatRangeLabel(
      timeframe?.comparison_window?.start || timeframe?.window?.comparison_start,
      timeframe?.comparison_window?.end || timeframe?.window?.comparison_end
    ) ||
    "Previous 28 days";
  const days =
    Number.isFinite(Number(timeframe?.window?.days))
      ? Number(timeframe.window.days)
      : Number.isFinite(Number(timeframe?.comparison_window?.days))
        ? Number(timeframe.comparison_window.days)
        : null;

  return {
    primaryLabel,
    comparisonLabel,
    why: timeframe?.why || "",
    days,
    primaryWindowStart:
      timeframe?.window?.start?.slice?.(0, 10) || "",
    primaryWindowEnd:
      timeframe?.window?.end?.slice?.(0, 10) || "",
    comparisonWindowStart:
      timeframe?.comparison_window?.start?.slice?.(0, 10) ||
      timeframe?.window?.comparison_start?.slice?.(0, 10) ||
      "",
    comparisonWindowEnd:
      timeframe?.comparison_window?.end?.slice?.(0, 10) ||
      timeframe?.window?.comparison_end?.slice?.(0, 10) ||
      "",
  };
};

const buildComparisonWindow = (window = {}) => {
  const days = Number(window?.days || 0);
  const start = window?.start ? new Date(window.start) : null;
  if (!days || !start || Number.isNaN(start.getTime())) return null;

  const comparisonEnd = new Date(start);
  comparisonEnd.setUTCDate(comparisonEnd.getUTCDate() - 1);
  const comparisonStart = new Date(comparisonEnd);
  comparisonStart.setUTCDate(comparisonStart.getUTCDate() - (days - 1));

  return {
    start: comparisonStart.toISOString(),
    end: comparisonEnd.toISOString(),
    days,
    label: formatRangeLabel(comparisonStart, comparisonEnd),
  };
};

const buildTimeframeFromWindow = (windowEntry = null, fallbackTimeframe = {}) => {
  const window = windowEntry?.window || null;
  if (!window?.start || !window?.end) return fallbackTimeframe || {};

  return {
    label: formatRangeLabel(window.start, window.end) || window.label || fallbackTimeframe?.label || "",
    comparisonLabel:
      buildComparisonWindow(window)?.label ||
      fallbackTimeframe?.comparisonLabel ||
      "",
    why:
      Number(window?.days) > 0
        ? `Homepage signals need breadth. Comparing ${window.days} days against the prior ${window.days}-day window smooths noise and captures behavior changes.`
        : fallbackTimeframe?.why || "",
    window: {
      ...window,
      label: formatRangeLabel(window.start, window.end) || window.label || "",
    },
    comparison_window:
      buildComparisonWindow(window) || fallbackTimeframe?.comparison_window || null,
  };
};

const buildHomepagePerformanceFromWindow = (windowEntry = null, fallbackMetrics = {}) => {
  const totals = windowEntry?.traffic_analytics?.totals || null;
  if (!totals) {
    return {
      ...fallbackMetrics,
      sessions: null,
      bounce_rate: null,
      avg_session_duration_sec: null,
      pages_per_session: null,
      ctr_to_pdp: null,
      exit_rate: null,
      new_visitors_share: null,
      returning_visitors_share: null,
    };
  }

  const activeUsers = toNumberOrNull(totals.activeUsers);
  const newUsers = toNumberOrNull(totals.newUsers);
  const returningUsers =
    activeUsers != null && newUsers != null ? Math.max(activeUsers - newUsers, 0) : null;
  const sessions =
    toNumberOrNull(totals.sessionsApprox) ??
    toNumberOrNull(totals.engagedSessions) ??
    activeUsers;
  const pageViews = toNumberOrNull(totals.screenPageViews);

  return {
    ...fallbackMetrics,
    sessions,
    bounce_rate: totals.bounceRate ?? null,
    avg_session_duration_sec: totals.averageSessionDurationSec ?? null,
    pages_per_session: sessions > 0 && pageViews != null ? pageViews / sessions : null,
    ctr_to_pdp: totals.ctr_to_pdp ?? null,
    exit_rate: (totals.exit_rate != null && totals.exit_rate > 0) ? totals.exit_rate : null,
    new_visitors_share: activeUsers > 0 && newUsers != null ? newUsers / activeUsers : null,
    returning_visitors_share:
      activeUsers > 0 && returningUsers != null ? returningUsers / activeUsers : null,
  };
};

const buildHomepageSeoFromWindow = (windowEntry = null, fallbackSeo = {}) => {
  const windowSeo = windowEntry?.homepage_seo || null;
  if (!windowSeo) {
    return {
      metrics: fallbackSeo?.metrics || {},
      queries: Array.isArray(fallbackSeo?.queries) ? fallbackSeo.queries : [],
      window: fallbackSeo?.window || null,
      coverage_status: fallbackSeo?.coverage_status || null,
      source: fallbackSeo?.source || "google_search_console",
    };
  }

  return {
    metrics: windowSeo?.metrics || {},
    queries: Array.isArray(windowSeo?.queries) ? windowSeo.queries : [],
    window: windowSeo?.window || null,
    coverage_status: windowSeo?.coverage_status || null,
    source: windowSeo?.source || fallbackSeo?.source || "google_search_console",
  };
};

const buildWindowScopedValue = (windowEntry = null, fieldName = "", fallbackValue = {}) => {
  const windowValue = fieldName ? windowEntry?.[fieldName] : null;
  if (windowValue && typeof windowValue === "object") {
    return windowValue;
  }
  return fallbackValue || {};
};

const getPreferredInitialWindowKey = (windows = [], defaultWindowKey = null) => {
  if (!Array.isArray(windows) || !windows.length) return defaultWindowKey || null;
  return (
    windows.find((windowEntry) => windowEntry?.key === "last_7_days")?.key ||
    defaultWindowKey ||
    windows[0]?.key ||
    null
  );
};

const HomepageAssessmentViewer = ({ data = {}, onWindowChange }) => {
  const {
    reporting_period: reportingPeriod = {},
    default_window_key: defaultWindowKey = null,
    available_windows: availableWindows = [],
    windows = [],
    timeframe = {},
    assessment_summary: assessmentSummary = {},
    homepage_performance: homepagePerformance = {},
    device_performance_split: devicePerformance = {},
    core_web_vitals: coreWebVitals = {},
    homepage_seo: homepageSeo = {},
    hero_and_copy_audit: heroAndCopyAudit = {},
    homepage_images_inventory: imageInventory = {},
    schema_quality: schemaQuality = {},
    web_branding: webBranding = {},
    competitive_context: competitiveContext = [],
  } = data;

  const initialWindowList = useMemo(() => {
    if (Array.isArray(windows) && windows.length) {
      return windows.map((windowEntry) => ({
        key: windowEntry?.key || "",
        days: windowEntry?.days || null,
        startDate:
          windowEntry?.startDate || windowEntry?.reporting_period?.startDate || null,
        endDate:
          windowEntry?.endDate || windowEntry?.reporting_period?.endDate || null,
        label: windowEntry?.label || windowEntry?.reporting_period?.label || "",
      }));
    }
    return [];
  }, [windows]);

  const [activeWindowKey, setActiveWindowKey] = useState(
    getPreferredInitialWindowKey(initialWindowList, defaultWindowKey)
  );

  useEffect(() => {
    setActiveWindowKey(getPreferredInitialWindowKey(initialWindowList, defaultWindowKey));
  }, [defaultWindowKey, initialWindowList]);

  const activeWindowData = useMemo(() => {
    if (!Array.isArray(windows) || !windows.length) return null;
    return (
      windows.find((windowEntry) => windowEntry?.key === activeWindowKey) ||
      windows.find((windowEntry) => windowEntry?.key === defaultWindowKey) ||
      windows[0] ||
      null
    );
  }, [windows, activeWindowKey, defaultWindowKey]);

  useEffect(() => {
    if (!activeWindowKey || !onWindowChange) return;
    if (activeWindowData?.key === activeWindowKey) return;
    const entry = initialWindowList.find((w) => w.key === activeWindowKey);
    onWindowChange({
      key: activeWindowKey,
      label: entry?.label || activeWindowKey,
      days: entry?.days || null,
      startDate: entry?.startDate || null,
      endDate: entry?.endDate || null,
      reportId: null,
      generationKey: data?.generation_key || null,
      reportKey: "homepageAssessment",
      collection: "homepage_assessment",
      section: "assessment",
      windowKey: activeWindowKey,
      assessmentKey: "homepage",
      assessmentLabel: "Homepage Assessment",
    });
  }, [activeWindowData?.key, activeWindowKey, data?.generation_key, initialWindowList, onWindowChange]);

  const performanceMetrics = buildHomepagePerformanceFromWindow(
    activeWindowData,
    homepagePerformance?.metrics || {}
  );
  const resolvedHomepageSeo = buildHomepageSeoFromWindow(activeWindowData, homepageSeo);
  const resolvedDevicePerformance = buildWindowScopedValue(
    activeWindowData,
    "device_performance_split",
    devicePerformance
  );
  const resolvedCoreWebVitals = buildWindowScopedValue(
    activeWindowData,
    "core_web_vitals",
    coreWebVitals
  );
  const resolvedImageInventory = buildWindowScopedValue(
    activeWindowData,
    "homepage_images_inventory",
    imageInventory
  );
  const seoMetrics = resolvedHomepageSeo?.metrics || {};
  const heroAssessments = Array.isArray(heroAndCopyAudit?.hero_assessments) ? heroAndCopyAudit.hero_assessments : [];
  const copyHierarchy = Array.isArray(heroAndCopyAudit?.copy_hierarchy)
    ? heroAndCopyAudit.copy_hierarchy.filter((item) => {
        const label = String(item?.label || "").trim().toUpperCase();
        return !["H1", "H2", "H3"].includes(label);
      })
    : [];
  const inventoryImages = Array.isArray(resolvedImageInventory?.images)
    ? resolvedImageInventory.images.filter((image) => Boolean(image?.linked_page_url))
    : [];
  const seoQueries = Array.isArray(resolvedHomepageSeo?.queries)
    ? resolvedHomepageSeo.queries
    : [];
  const competitionCards = Array.isArray(competitiveContext) ? competitiveContext : [];
  const resolvedTimeframe = resolveTimeframe(
    activeWindowData ? buildTimeframeFromWindow(activeWindowData, timeframe) : timeframe
  );
  const activeReportingPeriod =
    activeWindowData?.reporting_period ||
    (reportingPeriod?.startDate && reportingPeriod?.endDate ? reportingPeriod : null);
  const devicePerformanceSubtext =
    resolvedDevicePerformance?.time_window_applicable === false
      ? "Current homepage snapshot from PageSpeed Insights."
      : resolvedDevicePerformance?.summary || "Homepage performance by device.";
  const coreWebVitalsSubtext =
    resolvedCoreWebVitals?.time_window_applicable === false
      ? resolvedCoreWebVitals?.window?.label
        ? `Current homepage snapshot from PageSpeed Insights. Selected window: ${resolvedCoreWebVitals.window.label}.`
        : "Current homepage snapshot from PageSpeed Insights."
      : resolvedCoreWebVitals?.source || "Homepage field data";

  console.log("[HomepageAssessmentViewer] render", {
    collectionName: data?.collection_name || null,
    defaultWindowKey,
    activeWindowKey,
    availableWindowCount: Array.isArray(initialWindowList) ? initialWindowList.length : 0,
    rawWindowCount: Array.isArray(windows) ? windows.length : 0,
    availableWindowKeys: Array.isArray(initialWindowList)
      ? initialWindowList.map((windowEntry) => windowEntry?.key || null)
      : [],
    rawWindowKeys: Array.isArray(windows)
      ? windows.map((windowEntry) => windowEntry?.key || null)
      : [],
    hasHomepagePerformance: Boolean(homepagePerformance?.metrics),
    hasTimeframe: Boolean(timeframe && Object.keys(timeframe).length),
  });

  console.log("[HomepageAssessmentViewer] active window resolved", {
    activeWindowKey,
    activeWindowData,
    activeReportingPeriod,
    resolvedTimeframe,
    performanceMetrics,
    resolvedHomepageSeo,
  });

  return (
    <div className={styles.container}>
      <div className={styles.windowPickerRow}>
        {initialWindowList.length ? (
          <>
            <label
              htmlFor="homepage-assessment-window-select"
              className={styles.windowPickerLabel}
            >
              Time window
            </label>
            <select
              id="homepage-assessment-window-select"
              className={styles.windowPickerSelect}
              value={activeWindowKey || ""}
              onChange={(e) => setActiveWindowKey(e.target.value)}
            >
              {initialWindowList.map((windowEntry) => (
                <option key={windowEntry.key} value={windowEntry.key}>
                  {windowEntry.label}
                </option>
              ))}
            </select>
            {/* {activeReportingPeriod?.startDate && activeReportingPeriod?.endDate ? (
              <p className={styles.windowPickerMeta}>
                {activeReportingPeriod.label ? `${activeReportingPeriod.label} · ` : ""}
                {activeReportingPeriod.startDate} - {activeReportingPeriod.endDate}
              </p>
            ) : null} */}
          </>
        ) : (
          <>
            <div className={styles.windowPickerLabel}>Time window</div>
            <div className={styles.windowPickerStack}>
              {/* <div className={styles.windowPickerValue}>{resolvedTimeframe.primaryLabel}</div> */}
              {/* {(resolvedTimeframe.primaryWindowStart || resolvedTimeframe.primaryWindowEnd) ? (
                <p className={styles.windowPickerMeta}>
                  {resolvedTimeframe.primaryWindowStart} - {resolvedTimeframe.primaryWindowEnd}
                </p>
              ) : null} */}
            </div>
          </>
        )}
        {/* {resolvedTimeframe.days ? (
          <div className={styles.windowPickerBadge}>
            {resolvedTimeframe.days} day{resolvedTimeframe.days === 1 ? "" : "s"}
          </div>
        ) : null} */}
        {/* {resolvedTimeframe.why ? <p className={styles.windowPickerWhy}>{resolvedTimeframe.why}</p> : null} */}
      </div>

      <div className={`${styles.summaryCard} ${summaryToneClass(assessmentSummary?.tone)}`}>
        <p className={styles.summaryLabel}>Assessment summary</p>
        <p className={styles.summaryText}>{assessmentSummary?.text || "No homepage assessment summary available yet."}</p>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Homepage performance</h3>
          <span className={styles.sectionSub}>GA4 and Shopify · URL: /</span>
        </div>
        <div className={styles.metricGrid}>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Sessions</div><div className={styles.metricValue}>{formatInteger(performanceMetrics.sessions)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Bounce Rate</div><div className={styles.metricValue}>{formatPercent(performanceMetrics.bounce_rate)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>CTR to PDP</div><div className={styles.metricValue}>{formatPercent(performanceMetrics.ctr_to_pdp)}</div></div>
          {/* <div className={styles.metricCard}><div className={styles.metricLabel}>Exit Rate</div><div className={styles.metricValue}>{formatPercent(performanceMetrics.exit_rate)}</div></div> */}
          <div className={styles.metricCard}><div className={styles.metricLabel}>Avg Session Duration</div><div className={styles.metricValue}>{formatSeconds(performanceMetrics.avg_session_duration_sec)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Pages per Session</div><div className={styles.metricValue}>{formatDecimal(performanceMetrics.pages_per_session)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>New Visitors</div><div className={styles.metricValue}>{formatPercent(performanceMetrics.new_visitors_share)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Returning</div><div className={styles.metricValue}>{formatPercent(performanceMetrics.returning_visitors_share)}</div></div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Device performance split</h3>
          <span className={styles.sectionSub}>{devicePerformanceSubtext}</span>
        </div>
        <div className={styles.deviceGrid}>
          {["mobile", "desktop"].map((deviceKey) => {
            const device = resolvedDevicePerformance?.[deviceKey] || {};
            const title = deviceKey === "mobile" ? "Mobile" : "Desktop";
            return (
              <div key={deviceKey} className={styles.deviceCard}>
                <div className={styles.deviceHeader}>
                  <h4 className={styles.deviceTitle}>{title}</h4>
                  <span className={`${styles.score} ${scoreClass(device.performance_score)}`}>{formatInteger(device.performance_score)}</span>
                </div>
                <div className={styles.miniMetricGrid}>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Sessions</span><span className={styles.miniMetricValue}>{formatInteger(device.sessions)}</span></div>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Bounce</span><span className={styles.miniMetricValue}>{formatPercent(device.bounce_rate)}</span></div>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>CTR to PDP</span><span className={styles.miniMetricValue}>{formatPercent(device.ctr_to_pdp)}</span></div>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Duration</span><span className={styles.miniMetricValue}>{formatSeconds(device.avg_session_duration_sec)}</span></div>
                </div>
                {device.note ? <p className={styles.note}>{device.note}</p> : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Core web vitals</h3>
          <span className={styles.sectionSub}>{coreWebVitalsSubtext}</span>
        </div>
        <div className={styles.cwvGrid}>
          <div className={styles.cwvCard}>
            <div className={styles.cwvLabel}>Largest Contentful Paint</div>
            <div className={styles.cwvMetric}>LCP (mobile)</div>
            <div className={styles.cwvValue}>{formatMilliseconds(resolvedCoreWebVitals?.mobile?.lcp_ms)}</div>
            <div className={styles.cwvTarget}>Target: &lt; 2.5s</div>
          </div>
          <div className={styles.cwvCard}>
            <div className={styles.cwvLabel}>Interaction to Next Paint</div>
            <div className={styles.cwvMetric}>INP (mobile)</div>
            <div className={styles.cwvValue}>{formatMilliseconds(resolvedCoreWebVitals?.mobile?.inp_ms)}</div>
            <div className={styles.cwvTarget}>Target: &lt; 200ms</div>
          </div>
          <div className={styles.cwvCard}>
            <div className={styles.cwvLabel}>Cumulative Layout Shift</div>
            <div className={styles.cwvMetric}>CLS (homepage)</div>
            <div className={styles.cwvValue}>{formatDecimal(resolvedCoreWebVitals?.mobile?.cls ?? resolvedCoreWebVitals?.desktop?.cls, 2)}</div>
            <div className={styles.cwvTarget}>Target: &lt; 0.1</div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Homepage SEO</h3>
          <span className={styles.sectionSub}>
            {resolvedHomepageSeo?.window?.label
              ? `Search Console for ${resolvedHomepageSeo.window.label}`
              : "Search queries that landed users on /"}
          </span>
        </div>
        <div className={styles.metricGrid}>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Impressions</div><div className={styles.metricValue}>{formatInteger(seoMetrics.impressions)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Clicks</div><div className={styles.metricValue}>{formatInteger(seoMetrics.clicks)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>CTR</div><div className={styles.metricValue}>{formatPercent(seoMetrics.ctr, 2)}</div></div>
          <div className={styles.metricCard}><div className={styles.metricLabel}>Avg Position</div><div className={styles.metricValue}>{formatDecimal(seoMetrics.avg_position)}</div></div>
        </div>
        {seoQueries.length ? (
          <div className={styles.copyList}>
            {seoQueries.map((query, index) => (
              <div key={`${query.query || index}-${index}`} className={styles.copyRow}>
                <div className={styles.copyLabel}>{query.query || "Query"}</div>
                <div className={styles.copyText}>
                  Impr. {formatInteger(query.impressions)} · CTR {formatPercent(query.ctr, 2)} · Position {formatDecimal(query.position)}
                </div>
                <span className={`${styles.statusPill} ${toneClass(query.tone || "neutral")}`}>{query.status || "Tracked"}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Hero and copy audit</h3>
          <span className={styles.sectionSub}>Visual and text elements above the fold</span>
        </div>
        <div className={styles.heroGrid}>
          {heroAssessments.length ? heroAssessments.map((hero, index) => {
            const status = buildHeroStatus(hero);
            const heroImageUrl = getHeroImageUrl(hero);
            const heroCardTitle = getHeroCardTitle(hero, index);
            return (
              <div key={`${hero.id || index}-${index}`} className={styles.heroCard}>
                <div className={styles.heroPreview}>
                  {heroImageUrl ? (
                    <img
                      className={styles.heroImage}
                      src={heroImageUrl}
                      alt={hero.alt_text || hero.scraped_alt_text || heroCardTitle}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.imagePreview}>{heroCardTitle}</div>
                  )}
                </div>
                <div className={styles.cardHeader}>
                  <h4 className={styles.cardTitle}>{heroCardTitle}</h4>
                  <span className={`${styles.statusPill} ${toneClass(status.tone)}`}>{status.label}</span>
                </div>
                <div className={styles.miniMetricGrid}>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Alt Text</span><span className={styles.miniMetricValue}>{hero.alt_status || EMPTY}</span></div>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Image Quality</span><span className={styles.miniMetricValue}>{hero.quality || EMPTY}</span></div>
                  <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Alignment</span><span className={styles.miniMetricValue}>{hero.product_alignment || EMPTY}</span></div>
                </div>
                {hero.recommended_update ? <p className={styles.note}>{hero.recommended_update}</p> : null}
              </div>
            );
          }) : <p className={styles.emptyState}>No hero audit available yet.</p>}
        </div>
        <div className={styles.copyList}>
          {copyHierarchy.map((item, index) => (
            <div key={`${item.label || index}-${index}`} className={styles.copyRow}>
              <div className={styles.copyLabel}>{item.label || EMPTY}</div>
              <div className={`${styles.copyText} ${!item.text ? styles.copyTextMuted : ""}`}>{item.text || "No text detected"}</div>
              <span className={`${styles.statusPill} ${toneClass(item?.status?.tone)}`}>{item?.status?.label || "Unknown"}</span>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Homepage images inventory</h3>
          <span className={styles.sectionSub}>
            {formatInteger(inventoryImages.length)} linked merchandised images detected on homepage
          </span>
        </div>
        <div className={styles.imageGrid}>
          {inventoryImages.length ? inventoryImages.map((image, index) => {
            const imageTypeLabel = getInventoryTypeLabel(image);
            const imageLinkLabel = getInventoryLinkLabel(image);
            const hasLinkedPage = Boolean(image?.linked_page_url);
            return (
              <div key={`${image.url || index}-${index}`} className={styles.imageCard}>
                <div className={styles.imagePreview}>
                  {image.url ? (
                    <img
                      className={styles.imagePreviewImg}
                      src={image.url}
                      alt={image.alt_text || image.description || image.type || "Homepage image"}
                      loading="lazy"
                    />
                  ) : (
                    imageTypeLabel
                  )}
                </div>
                <div className={styles.imageBody}>
                  <div className={styles.cardHeader}>
                    <div className={styles.imageType}>{imageTypeLabel}</div>
                    {hasLinkedPage ? (
                      <span className={`${styles.statusPill} ${styles.statusNeutral}`}>
                        {imageLinkLabel}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.imageDescription}>
                    {image.description || image.alt_text || "No description available."}
                  </div>
                  <div className={styles.imageMetaGrid}>
                    <div className={styles.imageMetaItem}>
                      <span className={styles.imageMetaLabel}>Impressions</span>
                      <span className={styles.imageMetaValue}>{formatInteger(image.impressions)}</span>
                    </div>
                    <div className={styles.imageMetaItem}>
                      <span className={styles.imageMetaLabel}>Clicks</span>
                      <span className={styles.imageMetaValue}>{formatInteger(image.clicks)}</span>
                    </div>
                    <div className={styles.imageMetaItem}>
                      <span className={styles.imageMetaLabel}>CTR</span>
                      <span className={styles.imageMetaValue}>{formatPercent(image.ctr, 2)}</span>
                    </div>
                    <div className={styles.imageMetaItem}>
                      <span className={styles.imageMetaLabel}>Avg Position</span>
                      <span className={styles.imageMetaValue}>{formatDecimal(image.avg_position)}</span>
                    </div>
                  </div>
                  {image.linked_page_url ? (
                    <a
                      className={styles.imageLink}
                      href={image.linked_page_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {image.linked_page_url}
                    </a>
                  ) : null}
                </div>
              </div>
            );
          }) : <p className={styles.emptyState}>No homepage image inventory available yet.</p>}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Schema quality</h3>
          <span className={styles.sectionSub}>Structured data audit for search engines</span>
        </div>
        <div className={styles.schemaGrid}>
          <div className={styles.schemaCard}>
            <div className={styles.cardHeader}>
              <h4 className={styles.cardTitle}>Organization schema</h4>
              <span className={`${styles.statusPill} ${toneClass(schemaQuality?.organization?.quality === "poor" ? "negative" : schemaQuality?.organization?.quality === "fair" ? "warning" : "positive")}`}>{schemaQuality?.organization?.quality || EMPTY}</span>
            </div>
            <div className={styles.cardText}>{schemaQuality?.organization?.details?.recommendation || "Markup detected on homepage."}</div>
            {renderFieldPills(schemaQuality?.organization?.details?.missing_fields)}
          </div>
          <div className={styles.schemaCard}>
            <div className={styles.cardHeader}>
              <h4 className={styles.cardTitle}>Online store schema</h4>
              <span className={`${styles.statusPill} ${toneClass(schemaQuality?.online_store?.quality === "poor" ? "negative" : schemaQuality?.online_store?.quality === "fair" ? "warning" : "positive")}`}>{schemaQuality?.online_store?.quality || EMPTY}</span>
            </div>
            <div className={styles.cardText}>{schemaQuality?.online_store?.details?.recommendation || "Structured data coverage pending."}</div>
            {renderFieldPills(schemaQuality?.online_store?.details?.missing_fields)}
          </div>
        </div>
      </section>

      {/* <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Web branding benchmark</h3>
          <span className={styles.sectionSub}>How the homepage communicates your brand</span>
        </div>
        <div className={styles.brandingGrid}>
          <div className={styles.brandingCard}>
            <div className={styles.cardHeader}><h4 className={styles.cardTitle}>Homepage</h4></div>
            <div className={styles.cardText}>{webBranding?.description || "No homepage brand description available yet."}</div>
          </div>
          <div className={styles.brandingCard}>
            <div className={styles.miniMetricGrid}>
              <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Heading Levels</span><span className={styles.miniMetricValue}>{formatInteger(webBranding?.heading_levels)}</span></div>
              <div className={styles.miniMetricItem}><span className={styles.miniMetricLabel}>Announcement</span><span className={styles.miniMetricValue}>{webBranding?.announcement_live ? "Live" : "Missing"}</span></div>
            </div>
            {webBranding?.homepage_url ? <p className={styles.note}>{webBranding.homepage_url}</p> : null}
          </div>
        </div>
      </section> */}

      {competitionCards.length ? (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Competitive context</h3>
            <span className={styles.sectionSub}>Benchmarked against market context</span>
          </div>
          <div className={styles.competitiveGrid}>
            {competitionCards.map((item, index) => (
              <div key={`${item.title || index}-${index}`} className={styles.competitiveCard}>
                <div className={styles.cardHeader}>
                  <h4 className={styles.cardTitle}>{item.title || "Observation"}</h4>
                  <span className={`${styles.statusPill} ${toneClass(item.tone || "warning")}`}>{item.status || "Opportunity"}</span>
                </div>
                <div className={styles.cardText}>{item.body || item.summary || EMPTY}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default HomepageAssessmentViewer;
